import React, { useState } from 'react';
import { graphql } from 'react-relay';

import BaseLayout from '@zego/layouts/BaseLayout';
import withData from '@zego/lib/withData';
import ComingSoon from '@zego/pageContainers/ComingSoon';
import {
  BREAKPOINTS,
  getColWidthCalc,
  primaryColor,
  unit,
  unitTRBL,
  white,
} from '@zego/theme';
import ThemeZegoPurple from '@zego/themes/ThemeZegoPurple';
import { useTracking } from '@zego/tracking';

import H2 from '@zego/components/atoms/H2';
import Spacer from '@zego/components/atoms/Spacer';
import BlockListSection from '@zego/components/BlockListSection';
import Button from '@zego/components/button';
import SecondaryButton from '@zego/components/button/SecondaryButton';
import ContentContainer from '@zego/components/ContentContainer';
import FleetContactModal from '@zego/components/fleet/FleetContactModal';
import FleetPricingWhatsCovered from '@zego/components/fleet/FleetPricingWhatsCovered';
import FleetZegoReview from '@zego/components/fleet/FleetZegoReview';
import ProductHeroSection from '@zego/components/ProductHeroSection';
import SectionDisplay from '@zego/components/SectionDisplay';
import SubItemsSection from '@zego/components/SubItemsSection';

import heroImage from '@zego/static/images/hero-fleet.jpg';
import Cyclist from '@zego/static/images/products/motor/cyclist.svg';
import PrivateHire from '@zego/static/images/products/motor/private-hire.svg';
import Van from '@zego/static/images/products/motor/van.svg';
import Euro from '@zego/static/svgs/Euro.svg';
import hiwCyberIcon from '@zego/static/svgs/hiw-cyber-icon.svg';
import hiwTelematicsIcon from '@zego/static/svgs/hiw-telematics-icon.svg';
import iPhone from '@zego/static/svgs/iPhone.svg';
import ShieldTick from '@zego/static/svgs/ShieldTick.svg';

const HERO_ITEMS = {
  title: 'Bespaar tijd en geld met de flexibele wagenparkverzekering',
  description: `Bij Zego bieden we flexibele verzekeringen aan, gebaseerd op het gebruik van je wagenpark. Wij werken samen met de grootste spelers in de verzekeringsbranche om je eerlijke en transparante verzekeringen aan te bieden.`,
  overallWidth: '100%',
  heroImage: heroImage,
  benefits: [
    'Betaal per voertuig, afstand of tijd',
    'Flexibele maandelijkse facturatie',
    'Toegewijde account-, risico- en claimsteams',
  ],
};

const WHY_ZEGO_CONTENT = {
  title: 'Waarom Zego?',
  titleSize: 56,
  description:
    'Bij Zego kies je zelf de dekking, eigen risico en betaalwijze.',

  listData: [
    {
      icon: iPhone,
      heading: `Persoonlijke ondersteuning`,
      description: `Onze toegewijde teams (samen met onze toonaangevende partners –
      zoals Crawford, Van Ameyde, Swiss Re and Wakam) helpen je op weg en beschermen je premie.`,
      itemsPerRow: 3,
      textAlign: 'left',
    },
    {
      icon: ShieldTick,
      heading: `Verklein het risico`,
      description: `Ons platform verwerkt grote hoeveelheden data,
      zodat je eenvoudig de risico’s van je wagenpark en chauffeurs kunt inzien.
      Hierdoor worden ongelukken voorkomen, verbetert het rijgedrag van chauffeurs, en vermijd je stilstand van je wagenpark.`,
      itemsPerRow: 3,
      textAlign: 'left',
    },
    {
      icon: hiwCyberIcon,
      heading: `Altijd beschikbaar`,
      description: `Altijd toegang tot je verzekering –
      met onze gebruiksvriendelijke online omgeving kun je eenvoudig je verzekeringen beheren.
      Één-op-één support nodig? Ons team van specialisten staat voor je klaar.`,
      itemsPerRow: 3,
      textAlign: 'left',
    },
    {
      icon: Euro,
      heading: `Slim geprijsd`,
      description: `Betalen voor wat je gebruikt - met geavanceerde technologie en data
      maken wij premies op maat voor jouw voertuigen en chauffeurs, op basis van tijd en de afstand die zij afleggen.`,
      itemsPerRow: 3,
      textAlign: 'left',
    },
    {
      icon: hiwTelematicsIcon,
      heading: `Flexibele betaling`,
      description: `Betalen als het je bedrijf uitkomt –
      beheer je cashflow en uitgaven beter, door te kiezen voor maandelijkse of jaarlijkse facturatie.
      `,
      itemsPerRow: 3,
      textAlign: 'left',
    },
  ],
};

const INSURANCE_SOLUTIONS_TEXT = {
  title: 'Onze verzekeringen zijn zo uniek als jouw bedrijf',
  description:
    'Het maakt niet uit hoe groot je wagenpark is, of wat voor soort wagenpark je hebt. Onze verzekering helpt je op weg.',
  titleSize: 22,
  imageWidth: 50,
  items: [
    {
      image: PrivateHire,
      title: 'Taxi- en chauffeursdiensten',
    },
    {
      image: Van,
      title: 'Transport & Logistiek',
    },
    {
      image: Cyclist,
      title: 'Bedrijfs- en andere wagenparken',
    },
  ],
};

const WHATS_COVERED_TEXT = {
  header: `Wat wij dekken`,
  subHeader:
    'Onze polissen zijn erop gemaakt om jou en je wagenpark de bescherming te bieden die je nodig hebt. Maak je keuze tussen Uitgebreid, WA+Casco of WA voor de polis die het best bij je past.',
};

const COVERAGE_POINTS = [
  'schade aan eigendommen van derden (€7,5M per claim/verlies)',
  'verlies of schade aan je voertuig',
  'audio-, communicatie- en navigatieapparatuur',
  'ruitschade',
  'onbeperkte dekking van letselschade aan derden',
  'juridische kosten',
];

const REVIEW_TEXT = {
  reviewer: '- Nicko Williamson, CEO WeFlex.',
  quote:
    '“We hebben de keuze gemaakt om samen te werken met Zego, omdat ze een innovatieve speler in de verzekeringsmarkt zijn, die WeFlex een veel meer datagedreven, flexibele benadering aan verzekeringen kan geven.”',
};

const FleetNl = ({ viewer, query, netherlandsWebsiteFeature }) => {
  const [isContactOpen, setIsContactOpen] = useState(false);
  const { trackEvent } = useTracking();

  if (netherlandsWebsiteFeature?.variation?.value === 'show-coming-soon') {
    return (
      <ComingSoon
        country={query.country}
        emailAddress={viewer?.countryByCode?.supportEmail}
        telephoneNumber={viewer?.countryByCode?.formattedEnquiryNumber}
      />
    );
  }

  return (
    <BaseLayout
      viewer={viewer}
      title="Zego | Wagenpark"
      description="Flexibele verzekering voor jouw wagenpark"
    >
      <ThemeZegoPurple>
        <>
          <ProductHeroSection
            {...HERO_ITEMS}
            button={
              <Button
                onClick={event => {
                  event.preventDefault();
                  setIsContactOpen(true);
                  trackEvent('Fleet Landing Page, Quote - Button Clicked');
                }}
              >
                Meer informatie
              </Button>
            }
            margin={unitTRBL(10, 0, 10, 0)}
          />

          <Spacer height={unit(12)} />
          <SubItemsSection {...INSURANCE_SOLUTIONS_TEXT} />
          <Spacer height={unit(12)} />
          <BlockListSection {...WHY_ZEGO_CONTENT} />
          <Spacer height={unit(4)} />
          <FleetPricingWhatsCovered
            text={WHATS_COVERED_TEXT}
            coveragePoints={COVERAGE_POINTS}
          />
          <SectionDisplay
            css={{
              backgroundColor: primaryColor(11),
            }}
          >
            <ContentContainer
              id="contact"
              css={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <H2
                css={{
                  color: white,
                  textAlign: 'center',
                  marginBottom: unit(3),
                  fontSize: `32px`,
                  lineHeight: `40px`,
                  [BREAKPOINTS.medium]: {
                    marginBottom: unit(3),
                    width: getColWidthCalc(12, 20, 6),
                  },
                }}
              >
                Klaar om een verzekering te ontdekken die aansluit op jouw
                wagenpark&nbsp;?
              </H2>
              {!isContactOpen && (
                <SecondaryButton
                  onClick={event => {
                    event.preventDefault();
                    setIsContactOpen(true);
                    trackEvent('Fleet Landing Page, Quote - Button Clicked');
                  }}
                  css={{
                    width: getColWidthCalc(12, 20, 12),
                    marginLeft: 0,
                    [BREAKPOINTS.medium]: {
                      width: 'auto',
                    },
                  }}
                >
                  Lees meer
                </SecondaryButton>
              )}
            </ContentContainer>
            {isContactOpen && (
              <div>
                <FleetContactModal
                  campaignId={query?.SFCampaign}
                  landingPage="FleetNl"
                  redirectTo="/nl/fleet-form-success/"
                  errorRedirectTo="/nl/fleet-form-error/"
                  isOpen={isContactOpen}
                  onRequestClose={() => setIsContactOpen(false)}
                />
              </div>
            )}
          </SectionDisplay>
        </>
      </ThemeZegoPurple>
      <FleetZegoReview text={REVIEW_TEXT} />
      <Spacer height={unit(10)} />
    </BaseLayout>
  );
};

export default withData(FleetNl, {
  query: graphql`
    query FleetNlQuery($country: String!) {
      netherlandsWebsiteFeature: feature(name: "netherlands-website") {
        variation {
          ... on StringVariation {
            value
          }
        }
      }
      viewer {
        ...BaseLayout_viewer @arguments(country: $country)
        countryByCode(code: $country) {
          enquiryNumber
          formattedEnquiryNumber
          supportEmail
        }
      }
    }
  `,
  getHeaders: ({ netherlandsWebsiteFeature }) => {
    const netherlandsWebsiteStatus =
      netherlandsWebsiteFeature?.variation?.value;

    if (!netherlandsWebsiteStatus || netherlandsWebsiteStatus === 'hide') {
      return [404];
    }
  },
  getVariables: ({ query }) => {
    return {
      country: query.country,
    };
  },
});
